import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GuideArticleSection,
  GuideKeyTakeaways,
  GuideLayout,
} from "@/components/guides/GuideLayout";
import { BarcodeFigure } from "@/components/guides/BarcodeFigure";
import { Callout } from "@/components/guides/Callout";
import { Compare } from "@/components/guides/Compare";
import { TryTool } from "@/components/guides/TryTool";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { GUIDES_BY_SLUG } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const GUIDE = GUIDES_BY_SLUG["what-is-a-gtin"];

const TOC = [
  { id: "number-not-a-barcode", label: "A number, not a barcode" },
  { id: "anatomy", label: "Anatomy of a GTIN" },
  { id: "four-lengths", label: "The four lengths" },
  { id: "check-digit", label: "How the check digit works" },
  { id: "storing", label: "Storing GTINs without losing them" },
  { id: "when-new", label: "When you need a new GTIN" },
  { id: "gtin-meaning", label: "GTIN meaning" },
  { id: "gtin-vs-upc", label: "GTIN vs UPC" },
  { id: "how-to-get", label: "How to get a GTIN" },
];

const FAQ = [
  {
    q: "Is a GTIN the same as a barcode?",
    a: "No. A GTIN is a number — the global identifier for a product. A barcode is a visual symbol that encodes that number. The same GTIN can be printed as UPC-A, EAN-13, ITF-14 or carried inside a GS1 Data Matrix; it's still one GTIN.",
  },
  {
    q: "Where do I get a GTIN from?",
    a: "From your local GS1 member organisation. They allocate you a company prefix, and you assign the remaining digits to each of your products. Buying GTINs from resellers is risky — retailers increasingly require GS1-issued company prefixes for listing.",
  },
  {
    q: "What's the difference between GTIN-12, 13 and 14?",
    a: "GTIN-12 is a UPC-A. GTIN-13 is an EAN-13. GTIN-14 is the packaging/case hierarchy variant — same underlying product, but the leading digit (the indicator) signals 'case of N units'. All four lengths are GTINs and can be stored in a single 14-digit column.",
  },
  {
    q: "How Is the Check Digit Calculated?",
    a: "It's a mod-10 calculation: multiply alternating digits by 3 and 1 (right-to-left, starting at 3), sum them, and take the digit that brings the total to the next multiple of 10. Our Check Digit Calculator does the maths for you.",
  },
  {
    q: "Can a single product have more than one GTIN?",
    a: "A consumer unit has one GTIN. A case of 6 of them has a different GTIN (typically a GTIN-14). A different flavour or pack size needs a separate GTIN. Don't reuse a GTIN across substantially different product variants — search engines and retailers will reject duplicates.",
  },
  {
    q: "Can a GTIN Contain Letters?",
    a: "No. Every GTIN is made of the digits 0 to 9 only, whatever its length. Letters belong in other GS1 data printed next to it, such as a batch number under AI (10) or a serial number under AI (21), which accept alphanumeric characters. A product number with a letter in it is a typo or a different identifier, such as an internal SKU.",
  },
  {
    q: "Can a GTIN Start With 0?",
    a: "Yes, and many do. Plenty of UPC-A numbers begin with 0, and every GTIN-8, GTIN-12 or GTIN-13 written in the 14-digit form used under AI (01) starts with padding zeros. Those zeros are part of the number, so keep GTINs in text fields where a spreadsheet cannot strip them.",
  },
];

export const Route = createFileRoute("/barcode-guides/what-is-a-gtin")({
  component: WhatIsAGtinGuide,
  head: () => {
    const base = pageHead({
      title: GUIDE.title,
      description: GUIDE.description,
      path: GUIDE.path,
      ogType: "article",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: GUIDE.title,
          description: GUIDE.description,
          mainEntityOfPage: absoluteUrl(GUIDE.path),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
          datePublished: GUIDE.lastUpdated,
          dateModified: GUIDE.lastUpdated,
          about: GUIDE.keyword,
        }),
        ldScript(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Barcode Guides", path: "/barcode-guides" },
            { name: GUIDE.shortTitle, path: GUIDE.path },
          ]),
        ),
      ],
    };
  },
});

function WhatIsAGtinGuide() {
  return (
    <GuideLayout
      guide={GUIDE}
      toc={TOC}
      nextRead={[
        {
          to: "/barcode-guides/upc-vs-ean",
          label: "UPC vs EAN",
          desc: "Two flavours of the same GTIN, separated by one digit and forty years of geography.",
        },
        {
          to: "/barcode-guides/1d-vs-2d-barcodes",
          label: "1D vs 2D barcodes",
          desc: "When the GTIN is enough, and when you want to carry batch / expiry alongside it.",
        },
        {
          to: "/gtin-validator",
          label: "GTIN Validator",
          desc: "Check the length and mod-10 check digit of any GTIN-8, 12, 13 or 14.",
        },
        {
          to: "/check-digit-calculator",
          label: "Check Digit Calculator",
          desc: "Work out the last digit for a new GTIN from its prefix and item reference.",
        },
      ]}
    >
      <p>
        You list a new product on a marketplace and the form refuses it: <em>GTIN is invalid</em>.
        The number looks right. It came from your supplier&apos;s spec sheet. The thirteen digits
        even tally up to what was on the printed barcode. So what does &quot;invalid&quot; mean —
        and what exactly is a GTIN in the first place?
      </p>
      <p>
        GTIN stands for <strong>Global Trade Item Number</strong>. It is the number behind every
        UPC, EAN, ITF-14 and ISBN you have ever scanned. The barcode on the package is just
        packaging for the number — the GTIN itself is what ties a physical product to its master
        data in retailer systems, marketplaces and supply-chain feeds. Get the GTIN right and every
        downstream lookup works. Get it slightly wrong and you spend the rest of the afternoon
        wondering why one product is suddenly two SKUs.
      </p>

      <GuideArticleSection id="number-not-a-barcode" title="A Number, Not a Barcode">
        <p>
          The single most useful distinction in this whole topic is between the GTIN and the symbol
          that prints it. A GTIN is a sequence of digits. A barcode is a way of drawing that
          sequence so a scanner can read it. The same GTIN can wear several different uniforms —
          twelve-digit UPC-A on a US shelf, thirteen-digit EAN-13 on a UK shelf, fourteen-digit
          ITF-14 on the outer case, and a GS1 Digital Link URL inside a 2D code on the consumer pack
          — and it&apos;s still one number.
        </p>

        <BarcodeFigure
          format="ean13"
          value="5012345678900"
          caption={
            <>
              The worked example used throughout this guide: <code>5012345678900</code> — a GTIN-13,
              printed here as an EAN-13 barcode.
            </>
          }
        />

        <p>
          That mental model — GTIN as identity, barcode as packaging — keeps you out of trouble when
          teammates use the words interchangeably. If a retailer asks for &quot;the barcode&quot;,
          they almost always mean the GTIN. If a supplier sends you a CSV of GTINs, they may or may
          not include the GTIN-14 case form alongside the consumer-pack GTIN-13. Knowing
          they&apos;re the same number in different lengths is the difference between matching
          products and accidentally duplicating them.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="anatomy" title="Anatomy of a GTIN">
        <p>
          A GTIN is built from three parts: a GS1 company prefix (assigned to you), an item
          reference (assigned by you), and a check digit (calculated). Take the GTIN-13 we opened
          with:
        </p>
        <Compare
          panes={[
            {
              label: "Company prefix",
              caption: "Assigned by GS1 to the brand owner.",
              children: (
                <p>
                  <code>
                    <strong>5012345</strong>
                    <span>678900</span>
                  </code>
                </p>
              ),
            },
            {
              label: "Item reference + check",
              caption: "Item reference assigned by the brand; final digit is the check.",
              children: (
                <p>
                  <code>
                    <span>5012345</span>
                    <strong>67890</strong>
                    <em>0</em>
                  </code>
                </p>
              ),
            },
          ]}
        />
        <p>
          The split point between prefix and item reference moves depending on your allocation.
          Large brands receive short prefixes (sometimes only six digits) which leaves them room for
          hundreds of thousands of item references. Smaller brands receive longer prefixes and get
          fewer references in return. You don&apos;t need to know where the split sits to read or
          store a GTIN — you treat the whole number as opaque — but it&apos;s useful to know
          it&apos;s in there.
        </p>
        <Callout variant="spec" title="The prefix is not the country of manufacture">
          <p>
            People often read the leading digits of an EAN-13 as a country code. They identify the
            GS1 office that issued the prefix, not where the product was made. A brand registered
            with GS1 UK will have prefixes starting 500–509 whether it manufactures in Bristol or
            Shenzhen.
          </p>
        </Callout>
      </GuideArticleSection>

      <GuideArticleSection id="four-lengths" title="The Four Lengths">
        <p>
          GTIN comes in four lengths, all valid, all globally unique, all defined by GS1. The length
          you end up with depends on the company prefix you were issued and the packaging level
          you&apos;re identifying.
        </p>
        <Compare
          panes={[
            {
              label: "GTIN-12 · UPC-A",
              caption: "12 digits · the US-Canadian retail tradition.",
              children: (
                <BarcodeFigure format="upca" value="036000291452" formatLabel="GTIN-12 / UPC-A" />
              ),
            },
            {
              label: "GTIN-13 · EAN-13",
              caption: "13 digits · the global retail default.",
              children: (
                <BarcodeFigure
                  format="ean13"
                  value="5012345678900"
                  formatLabel="GTIN-13 / EAN-13"
                />
              ),
            },
          ]}
        />
        <Compare
          panes={[
            {
              label: "GTIN-14 · ITF-14",
              caption: "14 digits · cases and cartons, with an indicator digit at the front.",
              children: (
                <BarcodeFigure
                  format="itf14"
                  value="10614141000415"
                  formatLabel="GTIN-14 / ITF-14"
                />
              ),
            },
            {
              label: "GTIN-8 · EAN-8",
              caption: "8 digits · separate GS1 allocation for tiny packs.",
              children: (
                <BarcodeFigure format="ean8" value="96385074" formatLabel="GTIN-8 / EAN-8" />
              ),
            },
          ]}
        />
        <p>
          GTIN-12 and GTIN-13 identify the consumer unit you scan at a till. GTIN-14 identifies a
          packaging hierarchy above that — the case of six, the carton of twenty-four — and its
          leading digit (the &quot;indicator&quot;) tells you the level. GTIN-8 is reserved for
          items where there simply isn&apos;t room for thirteen digits of bars, like
          individually-wrapped sweets and electronic component packs.
        </p>
        <p>
          For deeper coverage of why UPC-A and EAN-13 are different in the first place, see the <Link to="/barcode-guides/upc-vs-ean">UPC vs EAN</Link> guide. For the spec-level
          reference to symbology behaviour, see the <Link to="/barcode-guides/barcode-formats">barcode formats field guide</Link>.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="check-digit" title="How the Check Digit Works">
        <p>
          The trailing digit of every GTIN is calculated from the others by a single mod-10 rule.
          It&apos;s the same algorithm for GTIN-8, 12, 13 and 14 — which is why one validator
          handles them all. Here&apos;s the math for <code>5012345678900</code>, walked through
          end-to-end:
        </p>
        <ol>
          <li>
            Take the body — the digits without the trailing check — <code>501234567890</code>.
          </li>
          <li>
            Read it right-to-left and multiply each digit alternately by 3 and 1, starting at 3:
            <br />
            <code>0×3 + 9×1 + 8×3 + 7×1 + 6×3 + 5×1 + 4×3 + 3×1 + 2×3 + 1×1 + 0×3 + 5×1</code>
            <br />
            <code>= 0 + 9 + 24 + 7 + 18 + 5 + 12 + 3 + 6 + 1 + 0 + 5 = 90</code>.
          </li>
          <li>
            The check digit is whatever brings that sum to the next multiple of ten. 90 is already a
            multiple of ten, so the check digit is <code>0</code>. Append it to the body and you
            have a full GTIN-13: <code>5012345678900</code>.
          </li>
        </ol>
        <Callout variant="in-practice" title="Why bother with a check digit?">
          <p>
            The mod-10 algorithm catches every single-digit typo and most adjacent transpositions
            (swapping two neighbouring digits) — by far the two most common ways humans mangle
            barcode numbers when re-keying them. It won&apos;t catch every error, but it costs one
            digit of address space and stops the most common data-entry mistakes for free.
          </p>
        </Callout>
        <p>
          You don&apos;t have to do this on paper. Paste the same value into the GTIN Validator and
          it&apos;ll confirm the length class and the check digit in one step:
        </p>
        <TryTool
          to="/gtin-validator"
          label="GTIN Validator"
          description="Validate any GTIN-8 / 12 / 13 / 14 against its mod-10 check digit."
          value="5012345678900"
        />
        <p>
          When you&apos;re building GTINs from a new company prefix — say you just received the
          prefix <code>501234</code> and want to mint item reference <code>56789</code> — the Check
          Digit Calculator gives you the trailing digit straight away:
        </p>
        <TryTool
          to="/check-digit-calculator"
          label="Check Digit Calculator"
          description="Compute the correct mod-10 check digit for a partial GTIN body."
          value="501234567890"
          cta="Compute for 501234567890"
        />
      </GuideArticleSection>

      <GuideArticleSection id="storing" title="Storing GTINs Without Losing Them">
        <p>
          The single biggest source of GTIN bugs has nothing to do with GS1 and everything to do
          with how the digits end up in a database. Two rules of thumb cover most of the failure
          modes:
        </p>
        <ol>
          <li>
            <strong>Store every GTIN as a 14-character string, left-padded with zeros.</strong> A
            GTIN-12 of <code>036000291452</code> goes in as <code>00036000291452</code>. A GTIN-13
            of <code>5012345678900</code> goes in as <code>05012345678900</code>. One column, one
            length, no ambiguity. You can render the shorter form for display or printing whenever
            you need to.
          </li>
          <li>
            <strong>Validate the check digit at the boundary.</strong> Any supplier feed, scanned
            input or free-text field gets verified before it is trusted. That catches transposed
            digits, stripped zeros and well-meaning spreadsheet auto-formatting in one pass.
          </li>
        </ol>
        <Callout variant="heads-up" title="Never store a GTIN as an integer">
          <p>
            A GTIN can have leading zeros that are significant. The moment a spreadsheet or database
            column casts it to a number, the zeros vanish and your joins start failing silently.
            Always treat GTINs as text, top-to-bottom, even if a column &quot;looks numeric&quot;.
          </p>
        </Callout>
        <p>
          When you receive a large supplier feed and want to verify every row at once, the Batch
          Barcode Tools page runs the same validation against a pasted list and gives you a flagged
          CSV back. Drop a list of GTIN-13s into it before importing and the typos surface in
          seconds.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="when-new" title="When you need a new GTIN">
        <p>
          A common mistake when launching a product variant is to reuse an existing GTIN
          &quot;because it&apos;s basically the same thing&quot;. Retailers and marketplaces
          actively police this and will reject listings. The rule from GS1 is conservative: anything
          a shopper would consider a different product needs a new GTIN. In practice that means:
        </p>
        <ul>
          <li>A different flavour, scent, colour or recipe needs a new GTIN.</li>
          <li>A different pack size (250 g vs 500 g) needs a new GTIN.</li>
          <li>
            A different packaging hierarchy (single can vs case of six) needs a new GTIN — usually a
            GTIN-14 with an indicator digit.
          </li>
          <li>A different language on the pack typically does not need a new GTIN.</li>
          <li>A promotional sleeve over the same product does not need a new GTIN.</li>
        </ul>
        <Callout variant="heads-up" title="Don't buy GTINs from resellers">
          <p>
            Third-party sites sell &quot;cheap UPCs&quot; that were minted decades ago by companies
            that no longer exist. Major marketplaces (Amazon, eBay) and large retailers increasingly
            require GS1-issued company prefixes and will delist products whose GTIN traces back to a
            reseller. Get your prefix from your national GS1 office directly — it&apos;s the only
            durable option.
          </p>
        </Callout>
      </GuideArticleSection>

      <GuideArticleSection id="gtin-meaning" title="GTIN Meaning: What Does GTIN Stand For?">
        <p>
          GTIN is short for Global Trade Item Number, the GS1 identification key for a trade item:
          any product or service that can be priced, ordered or invoiced at some point in the supply
          chain.
        </p>
        <p>
          The GTIN number meaning is tied to the product, not to the single unit. Every jar of the
          same jam shares one GTIN; telling one jar from another takes extra data, such as a batch
          number under Application Identifier (10) or a serial number under AI (21), printed next to
          the GTIN in a GS1-128 or a 2D barcode.
        </p>
        <p>
          The GTIN is one of several GS1 identification keys. The Global Location Number (GLN)
          identifies a company or a place, and the Serial Shipping Container Code (SSCC) identifies a
          single logistic unit such as a pallet. In a GS1 barcode, an item&apos;s own GTIN sits under
          AI (01) as 14 digits.
        </p>

        <h3>GTIN Full Form</h3>
        <p>
          The GTIN full form is Global Trade Item Number, and the number after the hyphen in GTIN-8,
          GTIN-12, GTIN-13 and GTIN-14 is the digit count. A GTIN-13 is simply a 13-digit Global
          Trade Item Number.
        </p>
        <p>
          GS1 introduced the GTIN name to bring the older UPC and EAN numbering systems under one
          key. That is why a GTIN code, an EAN code and a UPC can all describe the same product on a
          supplier form.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="gtin-vs-upc" title="GTIN vs UPC">
        <p>
          GTIN is the family of product numbers and UPC is one member of it: a UPC is the 12-digit
          GTIN-12, normally printed as a UPC-A barcode. Every UPC is a GTIN, but a GTIN in another
          length is not a UPC.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">GTIN and UPC compared by scope, length, use and storage.</caption>
            <thead>
              <tr>
                <th scope="col">Aspect</th>
                <th scope="col">GTIN</th>
                <th scope="col">UPC</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">What it names</th>
                <td>The GS1 product number in any length</td>
                <td>The GTIN-12 and the UPC-A and UPC-E barcodes</td>
              </tr>
              <tr>
                <th scope="row">Length</th>
                <td>8, 12, 13 or 14 digits</td>
                <td>12 digits</td>
              </tr>
              <tr>
                <th scope="row">Packaging levels</th>
                <td>Consumer units, cases and other trade items</td>
                <td>Mainly consumer units at retail checkout</td>
              </tr>
              <tr>
                <th scope="row">In GS1 data and AI (01)</th>
                <td>Always written as 14 digits</td>
                <td>Padded with two leading zeros</td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
        <p>
          The two words get swapped because US retail ran on UPCs for decades before the GTIN name existed. When a US marketplace asks for a UPC, it wants a GTIN-12; if your products carry GTIN-13s, the <Link to="/barcode-guides/upc-vs-ean">UPC vs EAN guide</Link> explains when a leading zero makes the two forms equivalent.
        </p>

        <h3>Is a GTIN the Same as a UPC?</h3>
        <p>
          No. Only the 12-digit GTIN-12 is a UPC; GTIN-8, GTIN-13 and GTIN-14 are separate lengths
          that a 12-digit UPC field cannot hold.
        </p>
        <p>
          A GTIN-14 with an indicator digit from 1 to 8 is the clearest case. It identifies a case of products rather than a consumer unit, so it has no UPC form even when the item inside carries one. Before you submit any of these numbers, the <Link to="/gtin-validator">GTIN Validator</Link> shows which length you have and whether its check digit is right.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="how-to-get" title="How to Get a GTIN">
        <p>
          You get GTINs by licensing a GS1 Company Prefix from the GS1 member organization in your
          country and numbering your products yourself. Some offices, such as GS1 US, also license
          single GTINs to brands with only a few products.
        </p>
        <ol>
          <li>
            Join the GS1 office for the country where your company is based, and choose a prefix capacity that covers every product and pack size you plan to sell.
          </li>
          <li>
            List each product variant and give it its own item reference. Keep that list in one place, because a GTIN must never be reassigned to a different product.
          </li>
          <li>
            Enter the prefix and item reference in the <Link to="/check-digit-calculator">Check Digit Calculator</Link> to get the final digit.
          </li>
          <li>
            Confirm the complete number in the <Link to="/gtin-validator">GTIN Validator</Link> before you send it to retailers or marketplaces.
          </li>
          <li>
            Print the GTIN as an EAN-13 or UPC-A barcode, for example with the <Link to="/barcode-generator">Barcode Generator</Link>.
          </li>
        </ol>
        <p>
          Fees depend on the GS1 office and the size of the prefix, and most offices charge a yearly
          renewal. A prefix licensed this way stays tied to your company, which is what lets
          retailers trace each GTIN code back to its brand owner.
        </p>
      </GuideArticleSection>

      <GuideKeyTakeaways
        items={[
          "GTIN is the identifier. The barcode is one of several ways to print it.",
          "Four lengths — GTIN-8, 12, 13 and 14 — all share the same check-digit algorithm and can live in a single 14-character column.",
          "Always store GTINs as left-padded strings, never as integers; leading zeros are significant.",
          "Validate every supplier-sourced GTIN at the boundary before it touches your master data.",
          "A new product variant — flavour, pack size, hierarchy — gets a new GTIN. Buy your prefix from GS1, not resellers.",
        ]}
      />

      <FaqSection heading="Frequently Asked Questions" items={FAQ} />
    </GuideLayout>
  );
}
